import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useReactToPrint } from 'react-to-print';
import {
  Dialog,
  Grid,
  DialogContent,
  Box,
  CircularProgress,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  DialogActions,
  Button,
  AccordionSummary,
  IconButton,
  DialogTitle,
  Switch,
  FormControlLabel,
} from '@mui/material';

import { styled } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import {
  saveIncidentsForm,
  setFormSettings,
  setSelectedIncidentInfo,
} from '../../../../store/incidentsReducer';
import { useDispatch } from 'react-redux';
import { capitalizeFirstLetter } from '../../../../utils/capitalizeFirstLetter';
import { useForm, FormProvider } from 'react-hook-form';
import FormSection, { checkCanBeNotApplicable } from './FormSection';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AccordionDetails from '@mui/material/AccordionDetails';
import CloseIcon from '@mui/icons-material/Close';
import {
  getSectionsByPathSuffix,
  createShouldShowField,
  extractDefaultValuesFromSettings,
  getAllErrorPaths,
  checkIsExpandedAccordion,
  getActiveMenuStyles,
  deepMergePreferFirst,
  findFieldInConfig,
  withIncidentClearFallback,
} from './utils';
import { IncidentContext } from './IncidentContext';
import BaseInfo from './BaseInfo';
import { incidentsApi } from '../../../../functions/IncidentsApi';
import { toast } from 'react-toastify';
import { BASE_FIRST_ID, INCIDENT_FIELDS_FOR_WATCHING } from './constants';
import { LoadingStatusMap } from '../../../../constants/state';
import LoadingButton from '@mui/lab/LoadingButton';
import { debounce } from 'lodash';
import CustomAccordion from './Accordion';
import ModalBoxV2 from '../../../Modal/ModalBoxV2';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-container': {
    background: '#2121214c',
  },
  '& .MuiPaper-root': {
    maxWidth: 1195,
    width: 1195,
  },
  '& .MuiDialogContent-root': {
    padding: theme.spacing(3),
    borderTop: 'none',
    scrollBehavior: 'smooth',
    '& p.title': {
      fontSize: '20px',
      fontWeight: 600,
      lineHeight: '22px',
      // marginBottom: "20px",
    },
    '& p.sectionTitle': {
      fontSize: '16px',
      fontWeight: 500,
      lineHeight: '18px',
      color: '#6B6B6B',
      marginBottom: '10px',
    },
    '& p.subTitle': {
      fontSize: '16px',
      fontWeight: 500,
      color: '#6B6B6B',
    },
    '& p.text': {
      fontSize: '16px',
      fontWeight: 500,
      color: '#000000df',
      marginTop: '10px',
      whiteSpace: 'wrap',
      wordBreak: 'break-all',
    },
    '& .unit-box': {
      minHeight: 145,
      background: '#F3F3F3B2',
      borderRadius: '4px',
      padding: '15px',
    },
    '& .unit-title': {
      fontSize: '14px',
      fontWeight: 600,
      color: 'rgba(0, 0, 0, 0.87)',
      marginBottom: '15px',
    },
    '& .unit-subtitle': {
      fontSize: '14px',
      fontWeight: 600,
      color: 'rgba(0, 0, 0, 0.57)',
      marginTop: '10px',
      marginBottom: '10px',
    },
    '& .unit-text': {
      fontSize: '14px',
      fontWeight: 400,
      color: 'rgba(0, 0, 0, 0.87)',
      marginTop: '8px',
      '& span': {
        fontWeight: 600,
      },
    },
    '& .form-input input': {
      borderColor: '#E4E4E7',
    },
    '& .MuiAccordion-root': {
      margin: '24px 0 0',
      width: '100%',
      maxWidth: '100%',
    },
  },
  '& .MuiDialogActions-root': {
    '& .actionButton': {
      borderColor: '#212121',
      color: '#212121',

      '&.MuiButton-contained': {
        background: '#212121',
        color: 'white',
        '&:disabled': {
          background: 'gray',
        },
      },
      '&.success': {
        '&.MuiButton-contained': {
          background: '#75ec9f',
          color: '#000000dd',
        },
      },
    },
  },
}));

const AIDS_FIELD_NAME = 'aids';

export const getFormSettings = async (params, signal) => {
  try {
    const response = await incidentsApi.formSettings(params, { signal });
    if (response.status === 200) {
      return response.data;
    } else throw response;
  } catch (err) {
    toast.error(err?.data?.message || 'Fetching error');
    console.log(err);
  }
};

const openOnlyOneBlockInForm = (formSettings, onToggleExpand) => {
  const firstBlock = formSettings?.flat();
  if (formSettings && !!firstBlock.length) {
    onToggleExpand(firstBlock[0].name, true);
  }
};

export default function EditIncidentModal({
  editIncident,
  setEditIncident,
  onRevalidateData,
}) {
  const {
    selectedIncidentInfo,
    incidentInfoFetching,
    formSettings,
    formSettingsFetching,
    formSaveFetching,
  } = useSelector((state) => state.incidentsReducer);
  const dispatch = useDispatch();
  const { info, settings: defaultSettings } = formSettings;

  // localSettings will change if the Incident type changes
  // use useLayoutEffect to set as a local variable
  // use useLayoutEffect instead of useEffect for fixing double re-rendering
  const [localSettings, setLocalSettings] = useState(defaultSettings);
  useLayoutEffect(() => {
    setLocalSettings(defaultSettings);
    // auto open block if it is only one
    openOnlyOneBlockInForm(defaultSettings, onToggleExpand);
  }, [defaultSettings]);

  const [confirmationModal, setConfirmationModal] = useState(null);
  const [isStructureLoading, setIsStructureLoading] = useState(false);
  const defaultIncidentType = useRef({
    primary_incident_type: '',
    incident_types: '',
  });
  const printRef = useRef(null);
  // remembers that the user already answered the Mutual Aid reminder,
  // so Validate Form and Save Report don't both ask for the same form state
  const mutualAidConfirmed = useRef(false);

  const [selectedSections, setSelectedSections] = useState([]);
  // sections the user marked as "Not applicable", keyed by section name
  const [notApplicableSections, setNotApplicableSections] = useState({});

  const onToggleExpand = useCallback((sectionName, isExpanded) => {
    setSelectedSections((prev) => {
      if (isExpanded) {
        // set sectionName as last in any case
        // menu use last item as current
        return [...prev.filter((name) => name !== sectionName), sectionName];
      }

      const allRelatedElementsInAccordion = [
        sectionName,
        ...getSectionsByPathSuffix(prev, sectionName),
      ];

      return prev.filter(
        (name) => !allRelatedElementsInAccordion.includes(name)
      );
    });
  }, []);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    onBeforePrint: () =>
      new Promise((resolve) => {
        if (document?.fonts?.ready) {
          document.fonts.ready.then(() => resolve());
        } else {
          resolve();
        }
      }),
    pageStyle: `
      @page { margin: 12mm; }
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      @media print {
        html, body { height: auto !important; overflow: visible !important; }
        #print-area { overflow: visible !important; height: auto !important; }
        #print-area .MuiAccordion-root { page-break-inside: avoid; }
        #print-area .MuiAccordion-root.Mui-expanded .MuiCollapse-root,
        #print-area .MuiAccordion-root.Mui-expanded .MuiCollapse-wrapper,
        #print-area .MuiAccordion-root.Mui-expanded .MuiCollapse-wrapperInner,
        #print-area .MuiAccordion-root.Mui-expanded .MuiAccordionDetails-root {
          height: auto !important;
          max-height: none !important;
          overflow: visible !important;
        }
        #print-area .MuiAccordion-root:not(.Mui-expanded) .MuiCollapse-root {
          display: none !important;
        }
      }
    `,
  });

  // use onse - for init data
  // then use logic from useEffect below (with getFormSettings) for update values and LocalSettings
  const values = useMemo(() => {
    if (!defaultSettings || !info) return {};
    return withIncidentClearFallback(
      extractDefaultValuesFromSettings(defaultSettings, info),
      info,
      defaultSettings
    );
  }, [defaultSettings, info]);

  const form = useForm({
    values,
    mode: 'onChange',
  });

  const { handleSubmit, reset, watch } = form;

  const handleNotApplicableChange = useCallback(
    (sectionName, isChecked) => {
      setNotApplicableSections((prev) => ({
        ...prev,
        [sectionName]: isChecked,
      }));

      // drop every item so the blank required fields stop blocking the report
      if (isChecked) {
        form.setValue(sectionName, []);
        form.clearErrors(sectionName);
      }
    },
    [form]
  );

  const [primary_incident_type, incident_types] = watch(
    INCIDENT_FIELDS_FOR_WATCHING
  );

  // update form structures
  // depend on primary_incident_type and incident_types
  const wasEffectOfIncidentTypeInit = useRef(false);
  useEffect(() => {
    const abortController = new AbortController();

    if (!primary_incident_type && !incident_types?.length) {
      return;
    }

    // check with default values
    if (
      // prevent update during the mounting component
      // but only if we already have primary_incident_type from server as default
      !wasEffectOfIncidentTypeInit.current &&
      !!values.base?.primary_incident_type
    ) {
      wasEffectOfIncidentTypeInit.current = true;
      return;
    }

    // check with previous values
    if (
      primary_incident_type ===
        defaultIncidentType.current.primary_incident_type &&
      //
      // use "or" operator for preventing double request
      // because backend can return an empty array for incident_types
      // after we set a new primary_incident_type,
      // but in that case there are incident_types as undefined in our prev state
      (incident_types || [])?.join('') ===
        (defaultIncidentType.current.incident_types || [])?.join('')
    ) {
      return;
    }

    const params = {};

    // in create mode selectedIncidentInfo doesn't exist
    if (!!selectedIncidentInfo) {
      params.incident = selectedIncidentInfo.id;
    }

    if (primary_incident_type) {
      params.primary_incident_type = primary_incident_type;
    }

    if (!!incident_types?.length) {
      params.incident_types = incident_types;
    }

    defaultIncidentType.current = {
      primary_incident_type,
      incident_types,
    };
    // set start loading
    setIsStructureLoading(true);

    getFormSettings(params, abortController.signal).then((formSettings) => {
      if (!formSettings) {
        return;
      }

      // save form values with the new structure
      const formValues = form.getValues();
      const mergedInfo = deepMergePreferFirst(formValues, formSettings.info);
      reset(
        withIncidentClearFallback(
          extractDefaultValuesFromSettings(formSettings.settings, mergedInfo),
          mergedInfo,
          formSettings.settings
        )
      );

      // set new settings (structure)
      setLocalSettings(formSettings.settings);

      // auto open block if it is only one
      openOnlyOneBlockInForm(defaultSettings, onToggleExpand);

      // set finish loading
      setIsStructureLoading(false);
    });

    return () => {
      abortController.abort();
    };
  }, [primary_incident_type, incident_types]);

  // clear state
  useEffect(() => {
    return () => {
      dispatch(setSelectedIncidentInfo(null));
      // object is default value for FormSettings
      dispatch(setFormSettings({}));
    };
  }, []);

  const closeHandler = () => {
    setEditIncident(false);

    reset(
      {},
      {
        keepErrors: false,
        keepDirty: false,
        keepTouched: false,
        keepIsValid: false,
        keepSubmitCount: false, // <--- resets submitCount
      }
    );

    setSelectedSections([]);
    setNotApplicableSections({});
    mutualAidConfirmed.current = false;
  };

  const onSubmit = (data) => {
    if (data) {
      const successCb = () => {
        closeHandler();
        onRevalidateData();
      };

      dispatch(
        saveIncidentsForm({
          id: selectedIncidentInfo?.id,
          data,
          successCb,
        })
      );
    }
  };

  // firefighters often forget to fill the aids field
  // just remind them if aids exist in form settings
  const shouldConfirmMutualAid = (data) =>
    !mutualAidConfirmed.current &&
    // the user already told us Mutual Aid does not apply, don't ask again
    !notApplicableSections[AIDS_FIELD_NAME] &&
    !!findFieldInConfig(AIDS_FIELD_NAME, localSettings) &&
    !data?.[AIDS_FIELD_NAME]?.length;

  const openMutualAidConfirmation = ({ primaryActionText, primaryAction }) => {
    const dismiss = () => {
      setConfirmationModal(null);
    };

    setConfirmationModal({
      title: 'Are you sure?',
      body: "The form is complete. Are you sure this call wasn't a Mutual Aid and you filled in all the info?",
      primaryActionText,
      primaryAction: () => {
        mutualAidConfirmed.current = true;
        setConfirmationModal(null);
        primaryAction();
      },
      secondaryActionText: 'Continue editing',
      secondaryAction: () => {
        dismiss();
        handleMenuItemClick(AIDS_FIELD_NAME);
      },
      onClose: dismiss,
    });
  };

  const onSaveReport = () => {
    handleSubmit(
      // set validate as "true" to validate on server
      (data) => {
        if (shouldConfirmMutualAid(data)) {
          openMutualAidConfirmation({
            primaryActionText: 'Save Report',
            primaryAction: () => {
              onSubmit({ ...form.getValues(), validate: true });
            },
          });
        } else {
          onSubmit({ ...data, validate: true });
        }
      },
      () => {
        setConfirmationModal({
          title: 'Are you sure?',
          body: 'The form is not complete. Do you want to save it as a draft?',
          primaryActionText: 'Save as draft',
          primaryAction: () => {
            const data = form.getValues();
            onSubmit({ ...data, validate: false });
            setConfirmationModal(null);
          },
          secondaryActionText: 'Continue editing',
          secondaryAction: () => {
            setConfirmationModal(null);
            handleValidation();
          },
          onClose: () => {
            setConfirmationModal(null);
            handleValidation();
          },
        });
      }
    )();
  };

  const scrollToError = (elementName) => {
    const field = document.getElementById(elementName);

    if (field) {
      field.focus({ preventScroll: true });
      field.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const handleMenuItemClick = (sectionName) => {
    // Find the accordion element by ID
    const accordionElement = document.getElementById(sectionName);

    if (accordionElement) {
      onToggleExpand(sectionName, true);

      // Scroll to the accordion with a small delay to ensure accordion expansion has started
      setTimeout(() => {
        accordionElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 0);
    }
  };

  const [validationStatus, setValidationStatus] = useState(
    LoadingStatusMap.INIT
  );
  // whenever the user edits any field after a validation attempt (success or error),
  // clear the previous validation result so the INIT.
  useEffect(() => {
    const unsub = form.watch(
      debounce(() => {
        // the form changed, so a previous Mutual Aid answer no longer applies
        mutualAidConfirmed.current = false;
        setValidationStatus((prev) =>
          prev !== LoadingStatusMap.INIT ? LoadingStatusMap.INIT : prev
        );
      }, 200)
    );
    return () => unsub.unsubscribe();
  }, [form]);

  const handleValidation = () => {
    // use trigger for rerender check required fields!
    // validation has problems - dependent fields in the group do not know that they have become required (or other rules) until the component is rerendered
    // A possible solution is to build a dependency tree outside the React components,
    // and a reverse tree that has a dependency in the keys, and in the values the IDs of the fields that depend on it (and update them)
    setValidationStatus(LoadingStatusMap.LOADING);

    // Open Mutual Aids along with the sections validation opens, so an
    // untouched section cannot be skipped. Expanding it lets the section's own
    // auto open put an item in front of the user.
    const isOpeningAids = !!findFieldInConfig(AIDS_FIELD_NAME, localSettings);
    if (isOpeningAids) {
      onToggleExpand(AIDS_FIELD_NAME, true);
    }

    // a freshly opened section mounts its item on a later render, so its fields
    // need a moment to register before there is anything to validate
    // 250 - same per level allowance the error scrolling below uses
    setTimeout(() => {
      handleSubmit(
        () => {
          setValidationStatus(LoadingStatusMap.SUCCESS);
        },
        (errors) => {
          // make all paths like string
          const allErrorPaths = getAllErrorPaths(errors);

          setSelectedSections(allErrorPaths);

          if (allErrorPaths.length) {
            // find max nesting level
            const maximumNesting = allErrorPaths.reduce((max, current) => {
              const dots = (current.match(/\./g) || []).length;
              return dots > max ? dots : max;
            }, 0);
            // each nested accordion use 200 ms
            // and subtract 1 to exclude base nesting levels
            // use Math.max for save positive result
            // 1 - default animation time
            const timeToScroll = Math.max(maximumNesting + 1) * 250;

            setTimeout(() => {
              scrollToError(allErrorPaths[0]);
              setValidationStatus(LoadingStatusMap.ERROR);
            }, timeToScroll);
          }
        }
      )();
    }, isOpeningAids ? 250 : 0);
  };

  return (
    <>
      <BootstrapDialog
        onClose={(_, reason) => {
          if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
            closeHandler();
          }
        }}
        aria-labelledby="view-incident-modal"
        open={!!editIncident}
        className="print"
      >
        <DialogTitle>
          <IconButton
            aria-label="close"
            onClick={closeHandler}
            sx={(theme) => ({
              position: 'absolute',
              right: 8,
              top: 8,
              color: '#212121',
              zIndex: '999',
            })}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {isStructureLoading && (
            <Box
              style={{
                position: 'absolute',
                top: '35px',
                left: 0,
                height: 'calc(100% - 84px)',
                width: '100%',
                zIndex: '999',
                backdropFilter: 'blur(3px)',
                WebkitBackdropFilter: 'blur(3px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CircularProgress />
            </Box>
          )}

          <Grid container spacing={2}>
            <Grid
              item
              xs={3}
              sx={{
                position: 'sticky',
                top: 0,
                alignSelf: 'flex-start',
                maxHeight: 'calc(100vh - 100px)',
                overflowY: 'auto',
                pr: 2,
                pt: '0 !important',
                scrollBehavior: 'smooth',
              }}
            >
              <Box
                sx={{
                  px: 2,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  background: '#F3F3F3',
                  borderRadius: '12px',
                  width: 'fit-content',
                  height: 51,
                }}
              >
                <Typography
                  sx={{
                    fontSize: '18px',
                    fontWeight: 600,
                    lineHeight: '20px',
                  }}
                >
                  {/*create title*/}
                  {!selectedIncidentInfo && !incidentInfoFetching && (
                    <>Create </>
                  )}{' '}
                  Incident
                  {/*edit title*/}
                  {selectedIncidentInfo?.dispatch.incident_number && (
                    <> {selectedIncidentInfo?.dispatch.incident_number}</>
                  )}
                </Typography>
              </Box>

              <List>
                {/* in create mode selectedIncidentInfo doesn't exist*/}
                {!!selectedIncidentInfo && (
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() => handleMenuItemClick(BASE_FIRST_ID)}
                      sx={{
                        height: '40px',
                        ...getActiveMenuStyles(selectedSections, BASE_FIRST_ID),
                      }}
                    >
                      <ListItemText primary="Base Report Info " />
                    </ListItemButton>
                  </ListItem>
                )}
                {localSettings?.flat().map((item) => {
                  const shouldShowBlock = createShouldShowField(
                    form.getValues,
                    item.name
                  )(item);
                  if (!shouldShowBlock) {
                    return;
                  }

                  return (
                    <ListItem disablePadding key={item.name}>
                      <ListItemButton
                        onClick={() => handleMenuItemClick(item.name)}
                        sx={{
                          height: '40px',
                          ...getActiveMenuStyles(selectedSections, item.name),
                        }}
                      >
                        <ListItemText
                          primary={capitalizeFirstLetter(
                            item.label || item.name
                          )}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </Grid>
            <Grid item xs={9}>
              {formSettingsFetching ? (
                <Box
                  sx={{ minHeight: '100px' }}
                  className="w-100 h-100 d-flex justify-content-center align-items-center"
                >
                  <CircularProgress />
                </Box>
              ) : (
                <form id="print-area" ref={printRef}>
                  {/* in create mode selectedIncidentInfo doesn't exist*/}
                  {!!selectedIncidentInfo && (
                    <CustomAccordion
                      /* "id" used in trigger accordion logic (handleMenuItemClick)*/
                      id={BASE_FIRST_ID}
                      defaultExpanded
                      variant="incident"
                    >
                      <AccordionSummary
                        variant="incident"
                        expandIcon={<ExpandMoreIcon />}
                        /* "id" used in trigger accordion logic (handleMenuItemClick)*/
                        id={`${BASE_FIRST_ID}-accordion`}
                        sx={{
                          p: 3,
                        }}
                      >
                        <Typography className="title">
                          Base Report Info
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails variant="incident">
                        <BaseInfo selectedIncidentInfo={selectedIncidentInfo} />
                      </AccordionDetails>
                    </CustomAccordion>
                  )}
                  <IncidentContext.Provider value={selectedIncidentInfo || {}}>
                    <FormProvider {...form}>
                      {localSettings?.flat().map((item) => {
                        const expanded = checkIsExpandedAccordion(
                          selectedSections,
                          item.name
                        );
                        const shouldShowBlock = createShouldShowField(
                          form.getValues,
                          item.name
                        )(item);

                        if (!shouldShowBlock) {
                          return null;
                        }

                        return (
                          <CustomAccordion
                            variant="incident"
                            key={item.name}
                            /* "id" used in trigger accordion logic (handleMenuItemClick)*/
                            id={item.name}
                            expanded={expanded}
                            onChange={(_, isExpanded) => {
                              onToggleExpand(item.name, isExpanded);
                            }}
                          >
                            <AccordionSummary
                              variant="incident"
                              expandIcon={<ExpandMoreIcon />}
                              /* "id" used in trigger accordion logic (handleMenuItemClick)*/
                              id={item.name}
                              sx={{
                                p: 3,
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 3,
                                }}
                              >
                                <Typography className="title">
                                  {capitalizeFirstLetter(
                                    item.label || item.name
                                  )}
                                </Typography>
                                {checkCanBeNotApplicable(item) && (
                                  <FormControlLabel
                                    /* keep the accordion from toggling when
                                       the switch is used */
                                    onClick={(event) => event.stopPropagation()}
                                    onFocus={(event) => event.stopPropagation()}
                                    sx={{ mr: 0 }}
                                    control={
                                      <Switch
                                        size="small"
                                        /* brand red - theme palette.primary */
                                        color="primary"
                                        checked={
                                          !!notApplicableSections[item.name]
                                        }
                                        onChange={(event) =>
                                          handleNotApplicableChange(
                                            item.name,
                                            event.target.checked
                                          )
                                        }
                                      />
                                    }
                                    label="Not applicable"
                                  />
                                )}
                              </Box>
                            </AccordionSummary>

                            <AccordionDetails
                              variant="incident"
                              onClick={() => {
                                //set the current item as active
                                onToggleExpand(item.name, true);
                              }}
                            >
                              <FormSection
                                form={form}
                                formSettingsItem={item}
                                expandedSections={selectedSections}
                                onToggleExpand={onToggleExpand}
                                notApplicableSections={notApplicableSections}
                              />
                            </AccordionDetails>
                          </CustomAccordion>
                        );
                      })}
                    </FormProvider>
                  </IncidentContext.Provider>
                </form>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Box
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <LoadingButton
              loading={validationStatus === LoadingStatusMap.LOADING}
              variant="contained"
              className={`actionButton ${
                validationStatus === LoadingStatusMap.SUCCESS ? 'success' : ''
              }`}
              onClick={handleValidation}
            >
              {validationStatus === LoadingStatusMap.SUCCESS
                ? 'Validation Successful'
                : 'Validate Form'}
            </LoadingButton>
            <Button
              variant="outlined"
              className="actionButton"
              onClick={() => {
                handlePrint();
              }}
            >
              Print
            </Button>
          </Box>
          <Box
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <Button
              variant="outlined"
              className="actionButton"
              onClick={() => setEditIncident(null)}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              className="actionButton"
              onClick={onSaveReport}
              disabled={formSaveFetching}
              startIcon={
                formSaveFetching ? (
                  <CircularProgress size={20} color="inherit" />
                ) : null
              }
            >
              {formSaveFetching ? 'Saving...' : 'Save Report'}
            </Button>
          </Box>
        </DialogActions>
      </BootstrapDialog>
      {confirmationModal && (
        <ModalBoxV2
          open={!!confirmationModal}
          onClose={confirmationModal.onClose}
          title={confirmationModal.title}
          body={confirmationModal.body}
          primaryActionText={confirmationModal.primaryActionText}
          primaryAction={confirmationModal.primaryAction}
          secondaryActionText={confirmationModal.secondaryActionText}
          secondaryAction={confirmationModal.secondaryAction}
        />
      )}
    </>
  );
}
